/**
 * Interne console met de dagelijkse paginascan (PageSpeed Insights / Lighthouse).
 *
 * Toont per pagina de vier Lighthouse-scores, het verschil met de vorige scan,
 * de labmetingen en het concrete verbeteradvies. Interne pagina: NL-only.
 */
/* i18n-ignore: interne adminpagina, alleen NL */
import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { getPageAuditDashboard, runPageAuditNow, type AuditPage } from "@/lib/page-audit.functions";
import {
  AUDIT_STRATEGIES,
  FIELD_METRICS,
  RATING_LABELS,
  SCORE_KEYS,
  SCORE_LABELS,
  adviceFor,
  formatSavings,
  pageLabel,
  rateFieldMetric,
  rateScore,
  type AuditStrategy,
  type FieldMetricKey,
  type Opportunity,
  type ScoreKey,
  type ScoreRating,
} from "@/lib/page-audit";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/pagespeed")({
  head: () => ({
    meta: [
      { title: "Paginascan | Beheer IT Solutions" },
      { name: "description", content: "Dagelijkse Lighthouse-scan met verbeteradvies per pagina." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Paginascan | Beheer IT Solutions" },
      { property: "og:type", content: "website" },
      { property: "og:description", content: "Dagelijkse Lighthouse-scan met verbeteradvies." },
    ],
  }),
  component: PageSpeedConsole,
});

const ratingClass: Record<ScoreRating, string> = {
  good: "bg-emerald-surface text-emerald-ink border-emerald-border",
  average: "bg-amber-surface text-amber-ink border-amber-border",
  poor: "bg-destructive/10 text-destructive border-destructive/30",
};

function ScorePill({ value }: { value: number | null }) {
  const rating = rateScore(value);
  if (value === null || !rating)
    return <span className="text-muted-foreground text-sm">&mdash;</span>;
  return (
    <span
      className={`inline-flex min-w-11 justify-center rounded-md border px-2 py-0.5 text-sm font-semibold tabular-nums ${ratingClass[rating]}`}
      title={RATING_LABELS[rating]}
    >
      {value}
    </span>
  );
}

function Delta({ now, before }: { now: number | null; before: number | null }) {
  if (now === null || before === null) return null;
  const diff = now - before;
  if (diff === 0) return null;
  const up = diff > 0;
  return (
    <span
      className={`ml-1 text-xs tabular-nums ${up ? "text-emerald-ink" : "text-destructive"}`}
      /* i18n-ignore: interne adminpagina */
      title="Verschil met vorige scan"
    >
      {up ? "+" : ""}
      {diff}
    </span>
  );
}

function fmtField(key: FieldMetricKey, value: number | null): string {
  if (value === null) return "—";
  if (key === "cls") return value.toFixed(3);
  if (value >= 1000) return `${(value / 1000).toFixed(2)} s`;
  return `${value} ms`;
}

function PageSpeedConsole() {
  const [strategy, setStrategy] = useState<AuditStrategy>("mobile");
  const [openPath, setOpenPath] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const fetchDashboard = useServerFn(getPageAuditDashboard);
  const startScan = useServerFn(runPageAuditNow);

  const { data, isLoading, error } = useQuery({
    queryKey: ["page-audit", strategy],
    queryFn: () => fetchDashboard({ data: { strategy } }),
  });

  const scan = useMutation({
    mutationFn: () => startScan({ data: { strategy } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["page-audit"] }),
  });

  const previousByPath = useMemo(() => {
    const map = new Map<string, AuditPage>();
    for (const p of data?.previousPages ?? []) map.set(p.path, p);
    return map;
  }, [data]);

  /** Sitebrede top: welk advies keert op de meeste pagina's terug? */
  const topAdvice = useMemo(() => {
    const map = new Map<string, { op: Opportunity; pages: string[]; savings: number }>();
    for (const page of data?.pages ?? []) {
      for (const op of page.opportunities ?? []) {
        const entry = map.get(op.id) ?? { op, pages: [], savings: 0 };
        entry.pages.push(page.path);
        entry.savings += op.savingsMs ?? 0;
        map.set(op.id, entry);
      }
    }
    return [...map.values()]
      .sort((a, b) => b.pages.length - a.pages.length || b.savings - a.savings)
      .slice(0, 8);
  }, [data]);

  const averages = useMemo(() => {
    const pages = data?.pages ?? [];
    const avg = (key: ScoreKey) => {
      const values = pages.map((p) => p[key]).filter((v): v is number => typeof v === "number");
      if (!values.length) return null;
      return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    };
    return SCORE_KEYS.map((key) => ({ key, value: avg(key) }));
  }, [data]);

  const latest = data?.latestRun ?? null;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10">
      <div className="mb-2 text-sm">
        {/* i18n-ignore */}
        <Link to="/admin/leads" className="text-muted-foreground hover:text-foreground">
          {/* i18n-ignore */}← Terug naar leads
        </Link>
        <span className="text-muted-foreground mx-2">|</span>
        {/* i18n-ignore */}
        <Link to="/admin/vitals" className="text-muted-foreground hover:text-foreground">
          Web Vitals
        </Link>
      </div>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold">Paginascan</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
            {/* i18n-ignore */}
            Dagelijkse Lighthouse-meting van de belangrijkste pagina&apos;s, met advies per punt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="border-border flex rounded-md border p-0.5">
            {AUDIT_STRATEGIES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStrategy(s)}
                className={`rounded px-3 py-1 text-sm capitalize ${
                  strategy === s ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <Button onClick={() => scan.mutate()} disabled={scan.isPending}>
            {/* i18n-ignore */}
            {scan.isPending ? "Bezig met scannen…" : "Scan nu"}
          </Button>
        </div>
      </header>

      {latest ? (
        <p className="text-muted-foreground mb-6 text-sm">
          {/* i18n-ignore */}
          Laatste scan: {new Date(latest.started_at).toLocaleString("nl-BE")} &middot;{" "}
          {latest.pages_ok} ok, {latest.pages_failed} mislukt &middot; {latest.trigger_source}
        </p>
      ) : null}

      {error ? (
        <p className="text-destructive text-sm">{(error as Error).message}</p>
      ) : isLoading ? (
        <p className="text-muted-foreground text-sm">Laden…</p>
      ) : !data?.pages.length ? (
        <div className="border-border rounded-lg border p-6">
          <p className="text-sm">
            {/* i18n-ignore */}
            Nog geen scanresultaten. Klik op &laquo;Scan nu&raquo; of wacht op de dagelijkse cron.
          </p>
        </div>
      ) : (
        <>
          <section className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {averages.map(({ key, value }) => (
              <div key={key} className="border-border rounded-lg border p-4">
                <p className="text-muted-foreground text-xs uppercase">{SCORE_LABELS[key]}</p>
                <div className="mt-2 flex items-baseline gap-2">
                  <ScorePill value={value} />
                  <span className="text-muted-foreground text-xs">gemiddeld</span>
                </div>
              </div>
            ))}
          </section>

          <section className="mb-8">
            <h2 className="font-display mb-3 text-xl font-semibold">Belangrijkste adviezen</h2>
            <ul className="space-y-2">
              {topAdvice.map(({ op, pages, savings }) => (
                <li key={op.id} className="border-border rounded-lg border p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="capitalize">
                      {op.category}
                    </Badge>
                    <span className="font-medium">{adviceFor(op)}</span>
                  </div>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {pages.length} pagina&apos;s
                    {savings >= 50 ? ` · ~${Math.round(savings)} ms totale winst` : ""}
                  </p>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-display mb-3 text-xl font-semibold">Per pagina</h2>
            <div className="border-border overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2">Pagina</th>
                    {SCORE_KEYS.map((key) => (
                      <th key={key} className="px-3 py-2">
                        {SCORE_LABELS[key]}
                      </th>
                    ))}
                    <th className="px-3 py-2">LCP</th>
                    <th className="px-3 py-2">CLS</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {data.pages.map((page) => {
                    const before = previousByPath.get(page.path);
                    const open = openPath === page.path;
                    return (
                      <Fragment key={page.path}>
                        <tr className="border-border border-t">
                          <td className="px-3 py-2">
                            <span className="font-medium">{pageLabel(page.path)}</span>
                            <span className="text-muted-foreground ml-2 text-xs">{page.path}</span>
                          </td>
                          {SCORE_KEYS.map((key) => (
                            <td key={key} className="px-3 py-2">
                              <ScorePill value={page[key]} />
                              <Delta now={page[key]} before={before?.[key] ?? null} />
                            </td>
                          ))}
                          <td className="px-3 py-2 tabular-nums">
                            {fmtField("lcp_ms", page.lcp_ms)}
                          </td>
                          <td className="px-3 py-2 tabular-nums">{fmtField("cls", page.cls)}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              className="text-primary text-xs underline"
                              onClick={() => setOpenPath(open ? null : page.path)}
                            >
                              {open ? "Sluiten" : "Advies"}
                            </button>
                          </td>
                        </tr>
                        {open ? (
                          <tr className="border-border border-t">
                            <td colSpan={8} className="bg-muted/30 px-3 py-4">
                              {page.error ? (
                                <p className="text-destructive text-sm">{page.error}</p>
                              ) : null}
                              <div className="mb-4 flex flex-wrap gap-3">
                                {FIELD_METRICS.map((m) => {
                                  const value = page[m.key] as number | null;
                                  const rating = rateFieldMetric(m.key, value);
                                  return (
                                    <span
                                      key={m.key}
                                      className={`rounded-md border px-2 py-1 text-xs ${
                                        rating ? ratingClass[rating] : "border-border"
                                      }`}
                                    >
                                      {m.label}: {fmtField(m.key, value)}
                                    </span>
                                  );
                                })}
                              </div>
                              <ul className="space-y-2">
                                {(page.opportunities ?? []).map((op) => (
                                  <li key={op.id} className="text-sm">
                                    <span className="font-medium">{adviceFor(op)}</span>
                                    <span className="text-muted-foreground">
                                      {" "}
                                      ({op.category}
                                      {formatSavings(op) ? `, ${formatSavings(op)}` : ""})
                                    </span>
                                  </li>
                                ))}
                                {!(page.opportunities ?? []).length ? (
                                  <li className="text-muted-foreground text-sm">
                                    {/* i18n-ignore */}
                                    Geen openstaande adviezen voor deze pagina.
                                  </li>
                                ) : null}
                              </ul>
                              <a
                                className="text-primary mt-3 inline-block text-xs underline"
                                href={`https://pagespeed.web.dev/report?url=${encodeURIComponent(page.url)}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {/* i18n-ignore */}
                                Volledig rapport op pagespeed.web.dev
                              </a>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
